import { AlertTriangle, Fuel } from 'lucide-react';
import { estimateFuel, fuelProblems } from '@jt/domain';

export type FuelInput = {
  litres: string;
  pricePerLitre: string;
  odometerStart: string;
  odometerEnd: string;
  daysCovered: string;
  jobsCovered: string;
};

export const EMPTY_FUEL: FuelInput = {
  litres: '',
  pricePerLitre: '',
  odometerStart: '',
  odometerEnd: '',
  daysCovered: '1',
  jobsCovered: '',
};

/** Blank means "not given", which is different from zero for every field here. */
const num = (value: string): number | null =>
  value.trim() === '' ? null : Number(value);

/** The shape the API takes, or null while the form is still incomplete. */
export function toFuelPayload(input: FuelInput) {
  const litres = num(input.litres);
  const pricePerLitre = num(input.pricePerLitre);
  const daysCovered = num(input.daysCovered);
  if (litres === null || pricePerLitre === null || daysCovered === null) return null;

  return {
    litres,
    pricePerLitre,
    odometerStart: num(input.odometerStart),
    odometerEnd: num(input.odometerEnd),
    daysCovered,
    jobsCovered: num(input.jobsCovered),
  };
}

/**
 * A tank of fuel, described rather than divided in somebody's head.
 *
 * The problem this replaces: a technician fills up on Monday, works until
 * Thursday, and has one receipt covering four days and eleven jobs. Asked for
 * "amount", they do the arithmetic themselves — and the two failure modes are
 * the same number every week, which is invented, or a different method every
 * week, which cannot be added up.
 *
 * So the technician supplies only what they can actually observe, and the split
 * is done the same way every time. The total is **computed, not typed**: two
 * numbers that should agree and might not is worse than one anybody can
 * re-check.
 *
 * The odometer pair is optional but pushed, because litres per 100 km is the
 * figure that catches a private weekend trip — the total looks perfectly
 * normal and the consumption does not.
 */
export function FuelClaim({
  value,
  onChange,
}: {
  value: FuelInput;
  onChange: (next: FuelInput) => void;
}) {
  const payload = toFuelPayload(value);
  const problems = payload ? fuelProblems(payload) : [];
  const estimate = payload && problems.length === 0 ? estimateFuel(payload) : null;

  const field = (
    key: keyof FuelInput,
    label: string,
    hint: string,
    extra: { step?: string; placeholder?: string } = {},
  ) => (
    <label className="block text-sm">
      <span className="mb-1 block font-medium">{label}</span>
      <input
        type="number"
        inputMode="decimal"
        step={extra.step ?? '0.01'}
        min="0"
        className="input w-full"
        placeholder={extra.placeholder}
        value={value[key]}
        onChange={(event) => onChange({ ...value, [key]: event.target.value })}
      />
      <span className="mt-0.5 block text-xs text-muted-foreground">{hint}</span>
    </label>
  );

  return (
    <div className="rounded-xl border border-primary-200 bg-primary-50/40 p-3">
      <h4 className="mb-1 flex items-center gap-1.5 text-sm font-semibold">
        <Fuel className="h-4 w-4 text-primary-600" aria-hidden="true" />
        The tank
      </h4>
      <p className="mb-3 text-xs text-muted-foreground">
        Put in what is on the pump slip and the dashboard. The amount is worked out from these, so
        you never have to divide a tank across days yourself.
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        {field('litres', 'Litres', 'What went into the tank', { placeholder: '40' })}
        {field('pricePerLitre', 'Price per litre (KES)', 'From the pump slip', {
          placeholder: '190',
        })}
        {field('daysCovered', 'Working days it covered', 'Monday to Thursday is 4', {
          step: '1',
          placeholder: '4',
        })}
        {field('jobsCovered', 'Jobs attended', 'Leave blank if you are not sure', {
          step: '1',
          placeholder: '11',
        })}
        {field('odometerStart', 'Odometer when you filled up', 'Optional, but it is the check', {
          step: '1',
          placeholder: '84200',
        })}
        {field('odometerEnd', 'Odometer now', 'Optional — give both or neither', {
          step: '1',
          placeholder: '84620',
        })}
      </div>

      {problems.length > 0 && (
        <ul className="mt-3 space-y-1">
          {problems.map((problem) => (
            <li key={problem} className="text-xs text-danger-600">
              {problem}
            </li>
          ))}
        </ul>
      )}

      {estimate && (
        <div className="mt-3 rounded-lg bg-card p-3">
          <p className="font-heading text-2xl font-bold tabular-nums text-primary-700">
            KES {estimate.total.toLocaleString('en-KE', { minimumFractionDigits: 2 })}
          </p>
          <p className="text-xs text-muted-foreground">{estimate.basis}</p>

          <dl className="mt-2 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
            {[
              ['Per day', `KES ${estimate.perDay.toLocaleString('en-KE')}`],
              ['Per job', estimate.perJob ? `KES ${estimate.perJob.toLocaleString('en-KE')}` : '—'],
              ['Per km', estimate.costPerKm ? `KES ${estimate.costPerKm}` : '—'],
              [
                'Litres/100km',
                estimate.consumptionPer100Km ? String(estimate.consumptionPer100Km) : '—',
              ],
            ].map(([label, figure]) => (
              <div key={label}>
                <dt className="text-xs text-muted-foreground">{label}</dt>
                <dd className="font-semibold tabular-nums">{figure}</dd>
              </div>
            ))}
          </dl>

          {/*
            Shown, not hidden. These do not block the claim — a person approves
            it, and this is what they should look at first. Telling the
            technician now is also fairer than raising it after they have
            submitted.
          */}
          {estimate.warnings.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {estimate.warnings.map((warning) => (
                <li
                  key={warning}
                  className="flex items-start gap-1.5 rounded-lg bg-accent-50 p-2 text-xs text-accent-700"
                >
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  {warning}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
